/**
 * Runtime configuration loader.
 *
 * Fetches /config.json at startup so the same build artifact can be
 * deployed to any environment — only the config.json served by the
 * hosting environment differs.
 *
 * Falls back to process.env.REACT_APP_* values (baked at build time)
 * so existing builds continue to work during the transition.
 */

export interface AppConfig {
  clientId: string;
  tenantId: string;
  redirectUri: string;
  permissionApiUrl: string;
  cadServiceUrl: string;
  crmServiceUrl: string;
  financeServiceUrl: string;
  sharepointServiceUrl: string;
  /** Backing service for the Support Insights tool (PHOTON-29). */
  freshdeskServiceUrl: string;
  /** Backing service for the JIRA Agent tool (PHOTON-76). */
  jiraServiceUrl: string;
  /** When true, chat inputs show a mic button for browser speech-to-text (photondev / test only). */
  enableVoiceInput: boolean;
  /** When true, the CRM tool shows the Tackle My Day tab (photondev only during build-out). */
  enableTackleMyDay: boolean;
  /** When true, the CRM tool shows the Calls tab and the Photon Dial extension can deep-link into it (photondev only during build-out). */
  enableCalls: boolean;
  /** When true, the Conferences & Shows tool appears (Allison's event_coordinator; photondev only during build-out). */
  enableConferences: boolean;
  /** When true, the DSMS6 tool appears (PHOTON-125; UI shell with no backend, photondev only during build-out). */
  enableDsms6: boolean;
  /** When true, TMD customer emails are created as drafts in the LERT's own Outlook
   *  (Microsoft Graph, delegated Mail.ReadWrite) instead of sent via GHL (PHOTON-117).
   *  Needs admin consent for Mail.ReadWrite on the app registration first. */
  enableOutlookDrafts: boolean;
}

let _config: AppConfig | null = null;
let _loading: Promise<AppConfig> | null = null;

function buildFallback(): AppConfig {
  return {
    clientId: process.env.REACT_APP_CLIENT_ID || "",
    tenantId: process.env.REACT_APP_TENANT_ID || "",
    redirectUri: window.location.origin,
    permissionApiUrl: process.env.REACT_APP_PERMISSION_API_URL || "/api",
    cadServiceUrl: process.env.REACT_APP_CAD_SERVICE_URL || "http://localhost:8001",
    crmServiceUrl: process.env.REACT_APP_CRM_SERVICE_URL || "",
    financeServiceUrl: process.env.REACT_APP_FINANCE_SERVICE_URL || "",
    sharepointServiceUrl:
      process.env.REACT_APP_SHAREPOINT_API_URL ||
      "https://sharepoint-service.redmoss-1c94587b.eastus2.azurecontainerapps.io",
    freshdeskServiceUrl: process.env.REACT_APP_FRESHDESK_SERVICE_URL || "",
    jiraServiceUrl: process.env.REACT_APP_JIRA_SERVICE_URL || "",
    enableVoiceInput: false,
    enableTackleMyDay: false,
    enableCalls: false,
    enableConferences: false,
    enableDsms6: false,
    enableOutlookDrafts: false,
  };
}

/**
 * Load the runtime configuration. Safe to call multiple times —
 * the fetch only happens once and concurrent callers share the same promise.
 */
export async function loadConfig(): Promise<AppConfig> {
  if (_config) return _config;
  if (_loading) return _loading;

  _loading = (async () => {
    try {
      const resp = await fetch("/config.json", { cache: "no-cache" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      _config = {
        clientId: json.clientId || "",
        tenantId: json.tenantId || "",
        redirectUri: window.location.origin,
        permissionApiUrl: json.permissionApiUrl || "/api",
        cadServiceUrl: json.cadServiceUrl || "",
        crmServiceUrl: json.crmServiceUrl || "",
        financeServiceUrl: json.financeServiceUrl || "",
        sharepointServiceUrl: json.sharepointServiceUrl || "",
        freshdeskServiceUrl: json.freshdeskServiceUrl || "",
        jiraServiceUrl: json.jiraServiceUrl || "",
        enableVoiceInput: Boolean(json.enableVoiceInput),
        enableTackleMyDay: Boolean(json.enableTackleMyDay),
        enableCalls: Boolean(json.enableCalls),
        enableConferences: Boolean(json.enableConferences),
        enableDsms6: Boolean(json.enableDsms6),
        enableOutlookDrafts: Boolean(json.enableOutlookDrafts),
      };
    } catch (err) {
      console.warn("Failed to load /config.json, falling back to build-time env:", err);
      _config = buildFallback();
    }
    return _config;
  })();

  return _loading;
}

/**
 * Synchronous getter — returns the config if it has already been loaded,
 * otherwise returns the build-time fallback. Call loadConfig() first
 * during app bootstrap to ensure the runtime values are available.
 */
export function getConfig(): AppConfig {
  return _config || buildFallback();
}
